import {
  CartErrorReason,
  MenuModuleErrorType,
  UnavailabilityReason,
} from '../constants/brandMenu';
import {
  ICartError,
  IMenuModuleUnavailableError,
  IMenuModuleUnmatchedError,
  IMUErrorResponse,
  IUnmatchedConstraintItem,
} from '../types/brandMenu';

// Human-readable copy per normalized reason. `node` is the leaf name of the
// failing cart node; `extra` carries reason-specific context (e.g. the
// unsatisfied constraint name for a missing modifier).
const buildMessage = (
  reasonCode: CartErrorReason,
  node: string,
  extra?: string
): string => {
  switch (reasonCode) {
    case CartErrorReason.notSold:
      return `${node} isn't available at this location.`;
    case CartErrorReason.outOfStock:
      return `${node} is temporarily out of stock.`;
    case CartErrorReason.unmatched:
      return `${node} isn't on this restaurant's menu.`;
    case CartErrorReason.missingModifier:
      return extra
        ? `${node} needs a selection for ${extra}.`
        : `${node} needs a required selection.`;
    default:
      return `${node} could not be added.`;
  }
};

export const getCartErrorMessage = buildMessage;

const reasonFromUnavailability = (
  reason?: UnavailabilityReason | string
): CartErrorReason =>
  reason === UnavailabilityReason.outOfStock
    ? CartErrorReason.outOfStock
    : CartErrorReason.notSold;

const leafOf = (path: string[]): string =>
  path.length ? path[path.length - 1] : '';

const toUnavailableErrors = (
  detail: IMenuModuleUnavailableError[]
): ICartError[] =>
  detail
    .filter((entry) => Array.isArray(entry?.path))
    .map((entry) => {
      const reasonCode = reasonFromUnavailability(entry.unavailability_reason);
      return {
        nodePath: entry.path,
        posId: entry.pos_id,
        reasonCode,
        message: buildMessage(reasonCode, leafOf(entry.path)),
      };
    });

const toUnmatchedErrors = (detail: IMenuModuleUnmatchedError): ICartError[] => {
  const constraintBuckets: IUnmatchedConstraintItem[] = [
    ...(detail.unmatched_top_items ?? []),
    ...(detail.unmatched_constraint_items ?? []),
  ];

  const fromConstraints: ICartError[] = constraintBuckets
    .filter((entry) => Array.isArray(entry?.path))
    .map((entry) => ({
      nodePath: entry.path,
      reasonCode: CartErrorReason.unmatched,
      message: buildMessage(CartErrorReason.unmatched, leafOf(entry.path)),
    }));

  // Bare top-level item names carry no path or alternatives.
  const fromNames: ICartError[] = (detail.unmatched_items ?? []).map(
    (name) => ({
      nodePath: [name],
      reasonCode: CartErrorReason.unmatched,
      message: buildMessage(CartErrorReason.unmatched, name),
    })
  );

  return [...fromConstraints, ...fromNames];
};

// invalid_constraints detail is a prose string, e.g.
// "Item blizzard has constraint errors: ['Constraint blizzard_flavor requires
//  at least 1 items, but only 0 found']"
const toInvalidConstraintErrors = (detail: string): ICartError[] => {
  const itemMatch = /^Item (.+?) has constraint errors:/.exec(detail);
  if (!itemMatch) {
    return [];
  }
  const itemName = itemMatch[1];
  const constraintMatch = /Constraint (\S+)/.exec(detail);
  const constraintName = constraintMatch?.[1];
  return [
    {
      nodePath: [itemName],
      reasonCode: CartErrorReason.missingModifier,
      message: buildMessage(
        CartErrorReason.missingModifier,
        itemName,
        constraintName
      ),
    },
  ];
};

const dedupeByNodePath = (errors: ICartError[]): ICartError[] => {
  const seen = new Set<string>();
  return errors.filter((error) => {
    const key = error.nodePath.join(' > ');
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/**
 * Whether a cart error belongs to a given cart line. Errors resolved against a
 * built cart carry a `cartItemId` (the HTTP order-response path); errors from
 * AI hypothesis frames don't (the cart is rebuilt every frame), so those fall
 * back to matching the top-level node name against the cart item name.
 */
export const cartErrorMatchesItem = (
  error: ICartError,
  cartItemId: number,
  itemName?: string
): boolean =>
  error.cartItemId !== undefined
    ? error.cartItemId === cartItemId
    : error.nodePath[0] === itemName;

/**
 * Error node paths for a given cart item, expressed relative to its modifier
 * tree (i.e. with the leading top-level item name dropped). The remaining
 * segments alternate modifier-group name / modifier-item name, matching the
 * cart tree by name (see convertToMenuModuleOrder / getMenuModuleConstraints).
 * Paths with no modifier segments (a bare top-level item) are excluded — those
 * are conveyed by the item-level failed styling instead.
 */
export const getModifierErrorPaths = (
  cartErrors: ICartError[],
  cartItemId: number,
  itemName?: string
): string[][] =>
  cartErrors
    .filter(
      (error) =>
        cartErrorMatchesItem(error, cartItemId, itemName) &&
        error.nodePath.length > 1
    )
    .map((error) => error.nodePath.slice(1));

/**
 * Normalize any menu-module error response into a flat, de-duped list of
 * cart-node errors the UI can render uniformly. Handles all three known
 * shapes: `unavailable_items` (array), `unmatched_items` (object), and
 * `invalid_constraints` (prose string). Any other reported failure — an
 * unrecognized error_type (e.g. 500 `unknown`, see MENU-1756) or a known
 * type whose detail couldn't be parsed — degrades to a single path-less
 * `unknown` error so it still surfaces in the order-level banner instead of
 * being silently dropped. Only a response with no error_type at all
 * normalizes to [].
 */
export const normalizeMenuModuleError = (
  response?: IMUErrorResponse
): ICartError[] => {
  if (!response) {
    return [];
  }
  const { error_type, detail } = response;

  let errors: ICartError[] = [];
  if (error_type === MenuModuleErrorType.unavailableItems) {
    errors = Array.isArray(detail)
      ? toUnavailableErrors(detail as IMenuModuleUnavailableError[])
      : [];
  } else if (error_type === MenuModuleErrorType.unmatchedItems) {
    errors =
      detail && !Array.isArray(detail) && typeof detail !== 'string'
        ? toUnmatchedErrors(detail as IMenuModuleUnmatchedError)
        : [];
  } else if (error_type === MenuModuleErrorType.invalidConstraints) {
    errors =
      typeof detail === 'string' ? toInvalidConstraintErrors(detail) : [];
  }

  if (errors.length === 0 && error_type) {
    return [
      {
        nodePath: [],
        reasonCode: CartErrorReason.unknown,
        message:
          typeof detail === 'string' && detail
            ? detail
            : `The order couldn't be processed (${error_type}).`,
      },
    ];
  }

  return dedupeByNodePath(errors);
};
